import React, { useCallback, useReducer } from 'react';
import { createGameReducer } from './createGameReducer';
import {
	CreateGameContext,
	createGameInitialState,
	TCreateGameSelectedItem,
	TCreateGameVisibleForm,
	TSourceUrlLocally,
	TTypeOfCreateGameModalWindow,
} from './createGameContext';
import { CreateGameActionTypes } from './createGameActions';
import {
	IItemForGetShortInfo,
	TFileId,
	TLanguageIDs,
	IFileData,
	IServerError,
	IGameApplicationIdValidationError,
	ICreateGame,
	IGameTranslation,
} from 'types';

const CreateGameState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createGameReducer, createGameInitialState);

	const changeVisibleForm = useCallback((value: TCreateGameVisibleForm) => {
		dispatch({
			type: CreateGameActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFile = useCallback((fileId: TFileId, sourceUrlLocally: TSourceUrlLocally) => {
		dispatch({
			type: CreateGameActionTypes.SET_FILE,
			payload: {
				fileId: fileId,
				sourceUrlLocally: sourceUrlLocally,
			},
		});
	}, []);

	const setFetchedDevelopers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateGameActionTypes.SET_FETCHED_DEVELOPERS,
			payload: {
				developers: value,
			},
		});
	}, []);

	const setIDDeveloper = useCallback((value: TCreateGameSelectedItem) => {
		dispatch({
			type: CreateGameActionTypes.SET_ID_DEVELOPER,
			payload: {
				IDDeveloper: value,
			},
		});
	}, []);

	const refetchDevelopers = useCallback((needRefetch: boolean) => {
		dispatch({
			type: CreateGameActionTypes.REFETCH_DEVELOPERS,
			payload: {
				needRefetch,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateGameActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				categories: value,
			},
		});
	}, []);

	const setIDCategory = useCallback((value: TCreateGameSelectedItem) => {
		dispatch({
			type: CreateGameActionTypes.SET_ID_CATEGORY,
			payload: {
				IDCategory: value,
			},
		});
	}, []);

	const changeApplicationId = useCallback((value: string) => {
		dispatch({
			type: CreateGameActionTypes.CHANGE_APPLICATION_ID,
			payload: {
				applicationId: value,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: CreateGameActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const changeVersionCode = useCallback((value: string) => {
		dispatch({
			type: CreateGameActionTypes.CHANGE_VERSION_CODE,
			payload: {
				versionCode: value,
			},
		});
	}, []);

	const changeVersionName = useCallback((value: string) => {
		dispatch({
			type: CreateGameActionTypes.CHANGE_VERSION_NAME,
			payload: {
				versionName: value,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: CreateGameActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const changeAppStoreLink = useCallback((value: string) => {
		dispatch({
			type: CreateGameActionTypes.CHANGE_APP_STORE_LINK,
			payload: {
				appStoreLink: value,
			},
		});
	}, []);

	const changePlayMarketLink = useCallback((value: string) => {
		dispatch({
			type: CreateGameActionTypes.CHANGE_PLAY_MARKET_LINK,
			payload: {
				playMarketLink: value,
			},
		});
	}, []);

	const changeFileData = useCallback((value: IFileData) => {
		dispatch({
			type: CreateGameActionTypes.CHANGE_FILE_DATA,
			payload: {
				fileData: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateGameActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				languages: value,
			},
		});
	}, []);

	const setIDsLanguages = useCallback(
		(value: TLanguageIDs[]) => {
			dispatch({
				type: CreateGameActionTypes.SET_IDs_LANGUAGES,
				payload: {
					IDsLanguages: value,
				},
			});

			const isNewLanguageAdded = value.length > state.translations.length;
			if (isNewLanguageAdded) {
				const existingTranslationIDs = state.translations.map((el) => el.languageId);
				const newTranslationID = value.find((el) => !existingTranslationIDs.includes(el));
				if (newTranslationID) {
					const newTranslation: IGameTranslation = {
						languageId: newTranslationID,
						name: '',
						description: '',
					};
					const updatedTranslations = [...state.translations, newTranslation];
					dispatch({
						type: CreateGameActionTypes.SET_TRANSLATION_FIELDS,
						payload: {
							updatedTranslations,
						},
					});
				}
			} else {
				const updatedTranslations = state.translations.filter((el) => value.includes(el.languageId));
				dispatch({
					type: CreateGameActionTypes.SET_TRANSLATION_FIELDS,
					payload: {
						updatedTranslations,
					},
				});
			}
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: ICreateGame | null) => {
		dispatch({
			type: CreateGameActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				data: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfCreateGameModalWindow) => {
		dispatch({
			type: CreateGameActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<IGameApplicationIdValidationError> | null) => {
		dispatch({
			type: CreateGameActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: CreateGameActionTypes.SET_TRANSLATION_TAB,
			payload: {
				translationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateGameActionTypes.CHANGE_TRANSLATION,
				payload: {
					updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeDescriptionTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							description: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateGameActionTypes.CHANGE_TRANSLATION,
				payload: {
					updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const resetFields = useCallback(() => dispatch({ type: CreateGameActionTypes.RESET_FIELDS }), []);

	return (
		<CreateGameContext.Provider
			value={{
				changeVisibleForm,
				setFile,
				setFetchedDevelopers,
				setIDDeveloper,
				refetchDevelopers,
				setFetchedCategories,
				setIDCategory,
				changeApplicationId,
				changeName,
				changeVersionCode,
				changeVersionName,
				changeDescription,
				changeAppStoreLink,
				changePlayMarketLink,
				changeFileData,
				setFetchedLanguages,
				setIDsLanguages,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				setTranslationTab,
				changeNameTranslation,
				changeDescriptionTranslation,
				resetFields,
				visibleForm: state.visibleForm,
				fileId: state.fileId,
				sourceUrlLocally: state.sourceUrlLocally,
				fetchedDevelopers: state.fetchedDevelopers,
				IDSelectedDeveloper: state.IDSelectedDeveloper,
				needRefetchDevelopers: state.needRefetchDevelopers,
				fetchedCategories: state.fetchedCategories,
				IDSelectedCategory: state.IDSelectedCategory,
				applicationId: state.applicationId,
				name: state.name,
				versionCode: state.versionCode,
				versionName: state.versionName,
				description: state.description,
				appStoreLink: state.appStoreLink,
				playMarketLink: state.playMarketLink,
				fileData: state.fileData,
				fetchedLanguages: state.fetchedLanguages,
				IDsSelectedLanguages: state.IDsSelectedLanguages,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
			}}>
			{children}
		</CreateGameContext.Provider>
	);
};

export default CreateGameState;
